/**
 * documentDraftFormatter.js
 *
 * Public API consumed by DocumentLibraryPage.
 *
 * buildDocumentFallbackHtml(doc, companyContext) → string
 *   Returns the per-document-ID prototype-quality preview draft from
 *   documentPreviews.js with company context filled.
 *
 * toDocumentHtml(doc, content, companyContext) → string
 *   Converts AI plain-text / HTML content into a rendered draft document.
 *   Falls back to buildDocumentFallbackHtml when content is empty / unusable.
 */

import { stripCodeFences, htmlToPlainText } from './draftUtils';
import { renderDraftHtml } from './draftRenderer';
import { getDocumentPreview } from './documentPreviews';

// ── Parse raw AI content into a structured spec ───────────────────────────────
function parseDocumentContent(doc, plainText) {
  const lines = plainText
    .replace(/\r\n/g, '\n')
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);

  let title = doc?.title || '[DOCUMENT TITLE]';
  let section = doc?.section || '';
  const bodyParagraphs = [];
  const resolvedClauses = [];
  const furtherResolvedClauses = [];

  lines.forEach((line) => {
    if (/^subject\s*:/i.test(line)) { title = line.replace(/^subject\s*:/i, '').trim() || title; return; }
    if (/^reference\s*:/i.test(line)) { section = line.replace(/^reference\s*:/i, '').trim() || section; return; }
    if (/^(date\s*:|venue\s*:|certified|for\s*\[|din\s*:|\[director|\[authorised)/i.test(line)) return;

    if (/^further resolved that/i.test(line)) { furtherResolvedClauses.push(line); return; }
    if (/^resolved that/i.test(line)) { resolvedClauses.push(line); return; }
    bodyParagraphs.push(line);
  });

  if (bodyParagraphs.length === 0) {
    bodyParagraphs.push(
      'This draft follows the ComplyV statutory format. Replace all [bracketed] placeholders with actual company and transaction data before final filing.',
    );
  }

  return { title, section, bodyParagraphs, resolvedClauses, furtherResolvedClauses };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Return the prototype-quality per-document preview with company context filled.
 * This is shown as the "Preview" draft and as the AI fallback when AI is unavailable.
 */
export function buildDocumentFallbackHtml(doc, companyContext = {}) {
  return getDocumentPreview(doc?.id || '', companyContext);
}

/**
 * Convert AI-returned content (plain text or HTML) into a rendered draft.
 * Falls back to buildDocumentFallbackHtml when content is empty / unusable.
 */
export function toDocumentHtml(doc, content = '', companyContext = {}) {
  const cleaned = stripCodeFences(content || '');
  if (!cleaned) return buildDocumentFallbackHtml(doc, companyContext);

  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(cleaned);
  const plainText = looksLikeHtml ? htmlToPlainText(cleaned) : cleaned;
  if (!plainText.trim()) return buildDocumentFallbackHtml(doc, companyContext);

  const { title, section, bodyParagraphs, resolvedClauses, furtherResolvedClauses } =
    parseDocumentContent(doc, plainText);

  return renderDraftHtml(
    {
      title,
      section,
      isResolution: String(doc?.category || '').toLowerCase() === 'resolution',
      bodyParagraphs,
      resolvedClauses,
      furtherResolvedClauses,
    },
    companyContext,
  );
}
